import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { 
  CreditCard, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  User,
  Calendar,
  DollarSign,
  Send,
  Unlock,
  Lock,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { API } from "../../utils/api";

const AdminPayments = () => {
  const { token } = useAuth();
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [processingAction, setProcessingAction] = useState(null);

  useEffect(() => {
    fetchPendingPayments();
  }, [token]);

  const fetchPendingPayments = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/pending-payments`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setPendingPayments(data);
      }
    } catch (error) {
      console.error("Error fetching pending payments:", error);
      toast.error("Erreur lors du chargement des paiements");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateUser = async (userId, userName) => {
    setProcessingAction(userId);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/users/${userId}/activate`, {
        method: "POST",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success(`Accès activé pour ${userName}`);
        fetchPendingPayments();
      } else {
        toast.error("Erreur lors de l'activation");
      }
    } catch (error) {
      console.error("Error activating user:", error);
      toast.error("Erreur lors de l'activation");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleBlockUser = async (userId, userName) => {
    setProcessingAction(userId);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/users/${userId}/block?reason=Paiement en attente`, {
        method: "POST",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success(`Accès bloqué pour ${userName}`);
        fetchPendingPayments();
      } else {
        toast.error("Erreur lors du blocage");
      }
    } catch (error) {
      console.error("Error blocking user:", error);
      toast.error("Erreur lors du blocage");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedUser || !paymentAmount) return;

    setProcessingAction(selectedUser.user_id);
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/record-payment`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          amount: parseFloat(paymentAmount),
          payment_method: paymentMethod,
          notes: paymentNotes
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Paiement enregistré pour ${selectedUser.name}`);
        setSelectedUser(null);
        setPaymentAmount("");
        setPaymentNotes("");
        fetchPendingPayments();
      } else {
        toast.error("Erreur lors de l'enregistrement du paiement");
      }
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error("Erreur lors de l'enregistrement du paiement");
    } finally {
      setProcessingAction(null);
    }
  };

  const getStatusBadge = (user) => {
    if (user.access_status === "pending") {
      return (
        <span className="flex items-center text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
          <Clock className="w-3 h-3 mr-1" />
          En attente
        </span>
      );
    } else if (user.access_status === "blocked") {
      return (
        <span className="flex items-center text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
          <XCircle className="w-3 h-3 mr-1" />
          Bloqué
        </span>
      );
    } else if (user.is_overdue) {
      return (
        <span className="flex items-center text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Retard {user.days_overdue}j
        </span>
      );
    } else if (user.payments_received < user.payments_total) {
      return (
        <span className="flex items-center text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
          <CreditCard className="w-3 h-3 mr-1" />
          {user.payments_received}/{user.payments_total}
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 p-6 lg:p-8 lg:ml-64" data-testid="admin-payments">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D] mb-2">
              Gestion des Paiements
            </h1>
            <p className="text-slate-600">
              Gérez les accès et les paiements des participants
            </p>
          </div>
          <Button
            onClick={fetchPendingPayments}
            variant="outline"
            className="border-[#101E4D] text-[#101E4D]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pending Payments List */}
          <div className="lg:col-span-2">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center text-[#101E4D]">
                  <CreditCard className="w-5 h-5 mr-2 text-[#FF6B35]" />
                  Paiements en attente ({pendingPayments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingPayments.length > 0 ? (
                  <div className="space-y-4">
                    {pendingPayments.map((user) => (
                      <div
                        key={user.user_id}
                        className={`p-4 rounded-xl border ${
                          user.is_overdue 
                            ? "border-red-200 bg-red-50" 
                            : user.access_status === "pending"
                            ? "border-yellow-200 bg-yellow-50"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              user.access_status === "active" ? "bg-green-500" :
                              user.access_status === "blocked" ? "bg-red-500" : "bg-yellow-500"
                            }`}>
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-[#101E4D]">{user.name}</p>
                              <p className="text-sm text-slate-500">{user.email}</p>
                              <div className="flex items-center space-x-2 mt-2">
                                {getStatusBadge(user)}
                                <span className="text-xs text-slate-400">
                                  Plan: {user.payment_plan?.toUpperCase() || "N/A"}
                                </span>
                                {user.total_amount && (
                                  <span className="text-xs text-slate-400">
                                    • {user.total_amount}€
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex space-x-2">
                            {user.access_status === "pending" && (
                              <Button
                                size="sm"
                                onClick={() => handleActivateUser(user.user_id, user.name)}
                                disabled={processingAction === user.user_id}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                {processingAction === user.user_id ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <>
                                    <Unlock className="w-4 h-4 mr-1" />
                                    Activer
                                  </>
                                )}
                              </Button>
                            )}
                            {user.access_status === "active" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setSelectedUser(user)}
                                  className="border-green-500 text-green-600 hover:bg-green-50"
                                >
                                  <DollarSign className="w-4 h-4 mr-1" />
                                  Paiement
                                </Button>
                                {user.is_overdue && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleBlockUser(user.user_id, user.name)}
                                    disabled={processingAction === user.user_id}
                                    className="bg-red-500 hover:bg-red-600 text-white"
                                  >
                                    <Lock className="w-4 h-4 mr-1" />
                                    Bloquer
                                  </Button>
                                )}
                              </>
                            )}
                            {user.access_status === "blocked" && (
                              <Button
                                size="sm"
                                onClick={() => handleActivateUser(user.user_id, user.name)}
                                disabled={processingAction === user.user_id}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                <Unlock className="w-4 h-4 mr-1" />
                                Réactiver
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {user.next_payment_due && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-xs text-slate-500 flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              Prochain paiement: {new Date(user.next_payment_due).toLocaleDateString('fr-FR')}
                              {user.is_overdue && (
                                <span className="text-red-600 ml-2 font-medium">
                                  (en retard de {user.days_overdue} jour{user.days_overdue > 1 ? 's' : ''})
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-300" />
                    <p className="text-lg font-medium">Tous les paiements sont à jour !</p>
                    <p className="text-sm">Aucun paiement en attente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payment Recording Form */}
          <div>
            <Card className="bg-white sticky top-6">
              <CardHeader>
                <CardTitle className="flex items-center text-[#101E4D]">
                  <DollarSign className="w-5 h-5 mr-2 text-green-500" />
                  Enregistrer un paiement
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedUser ? (
                  <form onSubmit={handleRecordPayment} className="space-y-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium text-[#101E4D]">{selectedUser.name}</p>
                      <p className="text-sm text-slate-500">{selectedUser.email}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Paiement {selectedUser.payments_received + 1}/{selectedUser.payments_total}
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="amount">Montant (€)</Label>
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="Ex: 350"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="method">Méthode de paiement</Label>
                      <select
                        id="method"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white"
                      >
                        <option value="bank_transfer">Virement bancaire</option>
                        <option value="card">Carte bancaire</option>
                        <option value="paypal">PayPal</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label htmlFor="notes">Notes (optionnel)</Label>
                      <Input
                        id="notes"
                        value={paymentNotes}
                        onChange={(e) => setPaymentNotes(e.target.value)}
                        placeholder="Ex: Paiement 2/3"
                      />
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSelectedUser(null)}
                        className="flex-1"
                      >
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        disabled={processingAction}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                      >
                        {processingAction ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "Enregistrer"
                        )}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Sélectionnez un utilisateur pour enregistrer un paiement</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPayments;
